import { useCallback } from "react";
import useFetch from "../use-fetch";
import { API_URL, AUTH_ME_URL } from "../config";
import { ApiBillingAddress, ShippingAddress, User } from "../types/user";
import wrapperFetchJsonResponse from "../wrapper-fetch-json-response";
import wrapperFetchStatusResponse from "../wrapper-fetch-status-response";
import { RequestConfigType } from "./types/request-config";
import {
    CheckoutDuplicateResponse,
    CheckoutFlowRequest,
    PendingCheckoutPurchase,
} from "~/types/Payment";
import type { PriceVariantKey } from "~/types/Checkout";

// The relations `/auth/me` can embed in the user.
export const ME_RELATIONS = {
    SUBSCRIPTIONS: "subscriptions",
    SHIPMENTS: "shipments",
    PURCHASES: "purchases",
} as const;

export type MeRelation = (typeof ME_RELATIONS)[keyof typeof ME_RELATIONS];

// What the app loads with the user, on sign-in and when relations are missing.
export const ALL_ME_RELATIONS: readonly MeRelation[] = Object.values(ME_RELATIONS);

export const buildAuthMeUrl = (include?: readonly MeRelation[] | MeRelation) => {
    const relations = include === undefined ? [] : [include].flat();
    if (relations.length === 0) return AUTH_ME_URL;

    return `${AUTH_ME_URL}?${relations.map((relation) => `include=${relation}`).join("&")}`;
};

export type AuthGetMeWithRelationsRequest = {
    include?: readonly MeRelation[] | MeRelation;
};

export type AuthGetMeWithRelationsResponse = User;

export function useAuthGetMeWithRelationsService() {
    const fetch = useFetch();

    return useCallback(
        (data: AuthGetMeWithRelationsRequest, requestConfig?: RequestConfigType) => {
            const url = buildAuthMeUrl(data.include);

            return fetch(url, {
                method: "GET",
                ...requestConfig,
            }).then(wrapperFetchJsonResponse<AuthGetMeWithRelationsResponse>);
        },
        [fetch]
    );
}

export type AuthPatchMeRequest =
    | Partial<Pick<User, "firstName" | "lastName">>
    | { password: string; oldPassword: string }
    | { phone: string }
    | { email: string }
    | { termsAccepted: boolean; termsAcceptedAt: Date }
    | { idCardVerifiedAt: Date }
    | { completedSurveyId: string }
    | { portalTourSeenAt: string }
    | {
          email: string;
          firstName: string;
          lastName: string;
          phone: string;
      } 
    | {
        shippingAddress: ShippingAddress
    }
    | {
        billingAddress: ApiBillingAddress
    };

export type AuthPatchMeResponse = User;

export function useAuthPatchMeService() {
    const fetch = useFetch();

    return useCallback(
        (data: AuthPatchMeRequest, requestConfig?: RequestConfigType) => {
            return fetch(`${AUTH_ME_URL}`, {
                method: "PATCH",
                body: JSON.stringify(data),
                ...requestConfig,
            }).then(wrapperFetchJsonResponse<AuthPatchMeResponse>);
        },
        [fetch]
    );
}

export type ChangeShipmentDateRequest = {
    // YYYY-MM-DD.
    shipmentAndBillingCycleDate: string;
    shipmentAndBillingCycleDateChangeReason?: string;
};

// Moves the next shipment of a subscription, and its billing cycle with it.
export function useChangeShipmentDateService() {
    const fetch = useFetch();

    return useCallback(
        (subscriptionId: string, data: ChangeShipmentDateRequest, requestConfig?: RequestConfigType) => {
            return fetch(`${AUTH_ME_URL}/subscription/${encodeURIComponent(subscriptionId)}`, {
                method: "PATCH",
                body: JSON.stringify(data),
                ...requestConfig,
            }).then(wrapperFetchStatusResponse);
        },
        [fetch]
    );
}

export type AuthGetMeResponse = {
    user?: User;
    token?: string;
    refreshToken?: string;
    tokenExpires?: number;
};

export type AuthGetLoginLinkRequest = {
    email?: string;
    phone?: string;
    returnTo?: string | undefined | null;
};

export function useAuthGetLoginLinkService() {
    const fetch = useFetch();

    return useCallback(
        (data: AuthGetLoginLinkRequest, requestConfig?: RequestConfigType) => {
            const requestUrl = new URL(`${API_URL}/v1/auth/send-login-link`);
            return fetch(requestUrl, {
                method: "POST",
                body: JSON.stringify(data),
                ...requestConfig,
            }).then(wrapperFetchJsonResponse<AuthGetMeResponse>);
        },
        [fetch]
    );
}

export type AuthLoginWithHashRequest = {
    hash: string;
    isAdminLogin?: boolean;
};

export function useAuthWithHashService() {
    const fetch = useFetch();

    return useCallback(
        (data: AuthLoginWithHashRequest, requestConfig?: RequestConfigType) => {
            const requestUrl = new URL(`${API_URL}/v1/auth/login-with-hash`);
            return fetch(requestUrl, {
                method: "POST",
                body: JSON.stringify({
                    ...data,
                    relations: ["shipments", "subscriptions"],
                }),
                ...requestConfig,
            }).then(wrapperFetchJsonResponse<AuthGetMeResponse>);
        },
        [fetch]
    );
}

export type AuthGetIntercomTokenRequest = {
    userId?: string;
};

export type AuthGetIntercomTokenResponse = {
    intercomChatToken?: string;
};

export function useAuthGetIntercomTokenService() {
    const fetch = useFetch();

    return useCallback(
        (
            data: AuthGetIntercomTokenRequest,
            requestConfig?: RequestConfigType
        ) => {
            return fetch(
                AUTH_ME_URL + `/intercom-chat-token?userId=${data.userId}`,
                {
                    method: "GET",
                    ...requestConfig,
                }
            ).then(wrapperFetchJsonResponse<AuthGetIntercomTokenResponse>);
        },
        [fetch]
    );
}

export type WaitListRequest = {
    name?: string;
    email: string;
    // age: number;
    // state: string;
};

export type WaitListResponse = void;

export function useWaitListService() {
    const fetch = useFetch();

    return useCallback(
        (data: WaitListRequest, requestConfig?: RequestConfigType) => {
            return fetch(`${API_URL}/v1/waitlists`, {
                method: "POST",
                body: JSON.stringify(data),
                ...requestConfig,
            }).then(wrapperFetchJsonResponse<WaitListResponse>);
        },
        [fetch]
    );
}

export type ProceedCheckoutResponse = PendingCheckoutPurchase &
    CheckoutDuplicateResponse & {
    clientSecret?: string;
    /**
     * Absent on a replay that can't sign the guest in again: a different or
     * missing `paymentIntentId`, or an original that issued no session.
     */
    token?: string;
    refreshToken?: string;
    tokenExpires?: number;
    amountPaid: number;
    user: User;
    externalInvoiceId?: string;
};

export type ProceedCheckoutRequest = CheckoutFlowRequest & {
    name: string;
    state: string;
    email: string;
    phone: string;
    paymentType?: string;
    priceItems: Array<{ slug: string; priceVariantKey?: PriceVariantKey }>;
    paymentIntentId?: string;
    paymentTokenId?: string;
    completedSurveyIds?: string[];
    couponId: string | undefined | null;
    userId: string | undefined | null;
    metaData: {
        guestData: Record<string, string> | undefined;
        userId: string | undefined | null;
    };
    dateOfBirth?: string;
    shippingAddress: ShippingAddress;
    billingAddress: ApiBillingAddress;
    reCAPTCHA?: string | undefined | null | unknown;
    eventData?: unknown;
};

export type CheckoutIntentRequest = {
    name: string;
    state: string;
    email: string;
    phone: string;
    promoCodeId: string | undefined | null;
    userId: string | undefined | null;
    metaData: {
        guestData: Record<string, string> | undefined;
        userId: string | undefined;
    };
    dateOfBirth?: string;
    shippingAddress: ShippingAddress;    
};

export function useProceedCheckoutService() {
    const fetchBase = useFetch();

    return useCallback(
        (data: ProceedCheckoutRequest, requestConfig?: RequestConfigType) => {
            return fetchBase(`${API_URL}/v1/auth/process-checkout`, {
                method: "POST",
                ...requestConfig,
                body: JSON.stringify(data),
            }).then(wrapperFetchJsonResponse<ProceedCheckoutResponse>);
        },
        [fetchBase]
    );
}
